import {
  EAgentRunStatus,
  MAX_LISTED_CHILD_RUNS,
  PARENT_RUN_LABEL_KEYS,
} from "../constants/automation";
import type {
  IAgentRunStatusDisplay,
  IAutomationChildRun,
  IChildRunCountField,
  IGetAgentRunKindLabelKeyProps,
  IGetAgentRunNoteKeysProps,
  IGetAgentRunStatusDisplayProps,
  IHasAgentRunHistoryLinkProps,
  IIsChildRunListTruncatedProps,
  IParentAutomationLists,
  IReadAgentRunResultsProps,
  IResolvedParentAutomation,
  IResolveParentAgentNameProps,
  IResolveParentAutomationProps,
  IRunAgentAndWaitResult,
  TAgentRunKindLabelKey,
  TAgentRunNoteKey,
  TParentRunLabelKey,
} from "../types/agent-invocation";
import {
  EAgentRunKind,
  EAgentRunResultStatus,
  EParentAutomationKind,
} from "../types/agent-invocation";
import { getOwnValue } from "./get-own-value";
import { isRecord } from "./type-guards";
import { isWorkflowRunActive } from "./workflow-run-status";

const isNullableString = (value: unknown): value is string | null =>
  value === null || typeof value === "string";

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && value.length > 0;

// NOTE: NSAI-5611 — the chat's `runAgentAndWait` tool returns this object on
// success and a plain string on a refusal or error, and the record reaches the
// client through a message payload the client does not own. So nothing is
// rendered as a run card until its shape is proven; anything else falls back
// to the reply's own text.
export const isRunAgentAndWaitResult = (
  value: unknown,
): value is IRunAgentAndWaitResult => {
  if (!isRecord(value)) {
    return false;
  }

  return (
    isNonEmptyString(value["agentUuid"]) &&
    typeof value["agentName"] === "string" &&
    typeof value["agentKind"] === "string" &&
    isNonEmptyString(value["runUuid"]) &&
    isNonEmptyString(value["status"]) &&
    isNullableString(value["output"]) &&
    typeof value["isOutputTruncated"] === "boolean" &&
    isNullableString(value["error"]) &&
    (value["note"] === undefined || typeof value["note"] === "string")
  );
};

// NOTE: Every valid run card on a message, in arrival order, one per run: a
// later record for the same run replaces the earlier one, so a redelivered
// payload never stacks a twin card.
export const readAgentRunResults = ({
  message,
}: IReadAgentRunResultsProps): IRunAgentAndWaitResult[] => {
  const { agentRunResults } = message;
  if (!Array.isArray(agentRunResults)) {
    return [];
  }

  const byRunUuid = new Map<string, IRunAgentAndWaitResult>();
  for (const entry of agentRunResults) {
    if (isRunAgentAndWaitResult(entry)) {
      byRunUuid.set(entry.runUuid, entry);
    }
  }
  return [...byRunUuid.values()];
};

// NOTE: literal keys (not built from the status) so the i18n keys stay
// statically greppable and type-checked against the chat namespace.
const AGENT_RUN_STATUS_DISPLAY: Record<
  EAgentRunResultStatus,
  IAgentRunStatusDisplay
> = {
  [EAgentRunResultStatus.OK]: {
    tone: "success",
    labelKey: "agent_run.status.ok",
    noteKey: null,
  },
  [EAgentRunResultStatus.ERROR]: {
    tone: "destructive",
    labelKey: "agent_run.status.error",
    noteKey: null,
  },
  [EAgentRunResultStatus.SKIPPED]: {
    tone: "warning",
    labelKey: "agent_run.status.skipped",
    noteKey: null,
  },
  [EAgentRunResultStatus.AWAITING_APPROVAL]: {
    tone: "warning",
    labelKey: "agent_run.status.awaiting_approval",
    noteKey: "agent_run.notes.awaiting_approval",
  },
  [EAgentRunResultStatus.RUNNING]: {
    tone: "warning",
    labelKey: "agent_run.status.running",
    noteKey: "agent_run.notes.still_running",
  },
  [EAgentRunResultStatus.STILL_RUNNING]: {
    tone: "warning",
    labelKey: "agent_run.status.still_running",
    noteKey: "agent_run.notes.still_running",
  },
  [EAgentRunResultStatus.WAIT_ABANDONED]: {
    tone: "neutral",
    labelKey: "agent_run.status.wait_abandoned",
    noteKey: "agent_run.notes.wait_abandoned",
  },
};

const UNKNOWN_STATUS_DISPLAY: IAgentRunStatusDisplay = {
  tone: "neutral",
  labelKey: null,
  noteKey: null,
};

export const getAgentRunStatusDisplay = ({
  status,
}: IGetAgentRunStatusDisplayProps): IAgentRunStatusDisplay =>
  getOwnValue({ record: AGENT_RUN_STATUS_DISPLAY, key: status }) ??
  UNKNOWN_STATUS_DISPLAY;

const AGENT_RUN_KIND_LABEL_KEYS: Record<EAgentRunKind, TAgentRunKindLabelKey> =
  {
    [EAgentRunKind.SCHEDULED_TASK]: "agent_run.kind.scheduled_task",
    [EAgentRunKind.WORKFLOW]: "agent_run.kind.workflow",
  };

export const getAgentRunKindLabelKey = ({
  kind,
}: IGetAgentRunKindLabelKeyProps): TAgentRunKindLabelKey =>
  getOwnValue({ record: AGENT_RUN_KIND_LABEL_KEYS, key: kind }) ??
  "agent_run.kind.unknown";

// NOTE: The explanatory lines under a run card: the status's own note, then
// the truncation notice when the model only got part of the output.
export const getAgentRunNoteKeys = ({
  result,
}: IGetAgentRunNoteKeysProps): TAgentRunNoteKey[] => {
  const noteKeys: TAgentRunNoteKey[] = [];
  const { noteKey } = getAgentRunStatusDisplay({ status: result.status });
  if (noteKey) {
    noteKeys.push(noteKey);
  }
  if (result.isOutputTruncated) {
    noteKeys.push("agent_run.notes.output_truncated");
  }
  return noteKeys;
};

// NOTE: Only scheduled tasks have a Run History on the clients' Agents page
// (workflows are not listed there), so only they get an "Open run" link.
export const hasAgentRunHistoryLink = ({
  kind,
}: IHasAgentRunHistoryLinkProps): boolean =>
  kind === EAgentRunKind.SCHEDULED_TASK;

// NOTE: NSAI-5609 — the name of the agent whose run started this one, from the
// automation lists the client already holds. Null when the parent is not one
// of them (a workflow, an agent the viewer cannot see, a deleted one): the
// caller then shows the generic "started by another agent" line.
export const resolveParentAgentName = ({
  parentAutomationUuid,
  automations,
}: IResolveParentAgentNameProps): string | null => {
  if (!parentAutomationUuid || !automations) {
    return null;
  }

  const parent = automations.find(
    (automation) => automation.uuid === parentAutomationUuid,
  );
  return parent?.name ? parent.name : null;
};

// NOTE: NSAI-7883 — whether a Run History row started other runs, so the card
// offers its "Child runs" list. A count from an older server (absent) or a
// malformed one reads as none rather than a list that would come back empty.
export const hasChildRuns = ({ childRunCount }: IChildRunCountField): boolean =>
  typeof childRunCount === "number" &&
  Number.isFinite(childRunCount) &&
  childRunCount > 0;

// NOTE: NSAI-7883 — a parent run's automation and its kind, looked up in the
// four lists the Agents page holds (in this order: an automation lives in
// exactly one). Null when none holds it — not shared with the viewer, or
// deleted — and the caller keeps the generic "another agent" line.
export const resolveParentAutomation = ({
  parentAutomationUuid,
  lists,
}: IResolveParentAutomationProps): IResolvedParentAutomation | null => {
  if (!parentAutomationUuid) {
    return null;
  }

  const candidates: readonly [
    EParentAutomationKind,
    IParentAutomationLists[keyof IParentAutomationLists],
  ][] = [
    [EParentAutomationKind.AGENT, lists.agents],
    [EParentAutomationKind.WORKFLOW, lists.workflows],
    [EParentAutomationKind.WEBHOOK, lists.webhooks],
    [EParentAutomationKind.EVENT_RULE, lists.eventRules],
  ];
  for (const [kind, automations] of candidates) {
    const name = resolveParentAgentName({ parentAutomationUuid, automations });
    if (name !== null) {
      return { name, kind };
    }
  }
  return null;
};

export const getParentRunLabelKey = (
  kind: EParentAutomationKind,
): TParentRunLabelKey => PARENT_RUN_LABEL_KEYS[kind];

// NOTE: NSAI-7883 — an expanded child list polls while any child is still
// going (an agent `running`, a workflow `running` or `waiting_approval`), so a
// finished child does not keep reading "running" until the history reopens.
export const hasActiveChildRun = (
  children: readonly IAutomationChildRun[] | undefined,
): boolean =>
  (children ?? []).some(
    (child) =>
      child.status === EAgentRunStatus.RUNNING ||
      isWorkflowRunActive(child.status),
  );

// NOTE: NSAI-7883 — the children read is capped, while the badge counts every
// readable child. When the list stops at the cap with more counted, the
// clients say so instead of passing the shorter list off as complete.
export const isChildRunListTruncated = ({
  listedCount,
  childRunCount,
}: IIsChildRunListTruncatedProps): boolean =>
  listedCount >= MAX_LISTED_CHILD_RUNS && childRunCount > listedCount;
